import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildsApi } from '../../api/builds';
import type { Build } from '../../api/builds';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import {
  Upload,
  CheckCircle,
  Clock,
  XCircle,
  Download,
  Trash2,
  RefreshCw,
  Zap,
  Activity,
  ChevronLeft,
  ChevronRight,
  Lock,
  Copy,
  Check,
} from 'lucide-react';

export function BuildsPage() {
  const [builds, setBuilds] = useState<Build[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [copiedPassword, setCopiedPassword] = useState<string | null>(null);

  const copyPassword = async (password: string) => {
    await navigator.clipboard.writeText(password);
    setCopiedPassword(password);
    setTimeout(() => setCopiedPassword(null), 2000);
  };

  useEffect(() => {
    loadBuilds();
  }, [page]);

  const loadBuilds = async () => {
    setLoading(true);
    try {
      const response = await buildsApi.list(page, 10);
      setBuilds(response.data || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Failed to load builds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (buildId: string) => {
    if (!confirm('Are you sure you want to delete this build?')) return;
    
    try {
      await buildsApi.delete(buildId);
      setBuilds(builds.filter((b) => b.id !== buildId));
    } catch (error) {
      console.error('Failed to delete build:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { icon: React.ReactNode; class: string; label: string }> = {
      COMPLETED: {
        icon: <CheckCircle className="w-3.5 h-3.5" />,
        class: 'badge-completed',
        label: 'Completed',
      },
      PROCESSING: {
        icon: <Clock className="w-3.5 h-3.5 animate-spin" />,
        class: 'badge-processing',
        label: 'Processing',
      },
      PENDING: {
        icon: <Clock className="w-3.5 h-3.5" />,
        class: 'badge-pending',
        label: 'Pending',
      },
      FAILED: {
        icon: <XCircle className="w-3.5 h-3.5" />,
        class: 'badge-failed',
        label: 'Failed',
      },
      EXPIRED: {
        icon: <Clock className="w-3.5 h-3.5" />,
        class: 'bg-slate-700/50 text-slate-400 border border-slate-600/50',
        label: 'Expired',
      },
    };
    
    const config = configs[status] || configs.PENDING;
    return (
      <span className={`badge ${config.class}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 opacity-0-init animate-fade-in-up">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-2 font-mono">
              <Activity className="w-4 h-4 text-cyan-500" />
              <span>Build History</span>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">Your Builds</h1>
            <p className="text-slate-400 text-sm sm:text-base">Manage and download your protected binaries</p>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button variant="ghost" onClick={loadBuilds} className="flex-1 sm:flex-none">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Link to="/builds/new" className="flex-1 sm:flex-none">
              <Button glow className="w-full sm:w-auto">
                <Zap className="w-4 h-4" />
                New Build
              </Button>
            </Link>
          </div>
        </div>

        {/* Builds list */}
        <Card className="opacity-0-init animate-fade-in-up animation-delay-100">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="relative w-12 h-12 mb-4">
                <div className="absolute inset-0 rounded-full border-2 border-slate-800" />
                <div className="absolute inset-0 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin" />
              </div>
              <p className="text-slate-500 font-mono text-sm">Loading builds...</p>
            </div>
          ) : builds.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-800/50 flex items-center justify-center border border-slate-700/50">
                <Upload className="w-10 h-10 text-slate-600" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No builds yet</h3>
              <p className="text-slate-500 mb-6">Create your first protected build now</p>
              <Link to="/builds/new">
                <Button glow>
                  <Zap className="w-4 h-4" />
                  Create Your First Build
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="lg:hidden space-y-3">
                {builds.map((build, index) => (
                  <div
                    key={build.id}
                    className="p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 space-y-3 opacity-0-init animate-fade-in-up"
                    style={{ animationDelay: `${0.1 + index * 0.05}s` }}
                  >
                    {/* Header with filename and status */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-white text-sm truncate">{build.filename}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-purple-400 font-mono text-xs">
                            {build.engineType}
                            {build.engineVersion && (
                              <span className="text-slate-500"> v{build.engineVersion}</span>
                            )}
                          </span>
                        </div>
                      </div>
                      {getStatusBadge(build.status)}
                    </div>
                    
                    {/* Watermark if exists */}
                    {build.watermark && (
                      <div>
                        <span className="text-xs text-slate-500">Watermark: </span>
                        <code className="text-cyan-400 text-xs bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20">
                          {build.watermark}
                        </code>
                      </div>
                    )}

                    {/* Zip Password if exists */}
                    {build.zipPassword && (
                      <div className="flex items-center gap-2">
                        <Lock className="w-3 h-3 text-amber-400" />
                        <span className="text-xs text-slate-500">Password: </span>
                        <code className="text-amber-400 text-xs bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                          {build.zipPassword}
                        </code>
                        <button
                          onClick={() => copyPassword(build.zipPassword!)}
                          className="p-1 rounded hover:bg-slate-700/50 text-slate-500 hover:text-white transition-all"
                          title="Copy password"
                        >
                          {copiedPassword === build.zipPassword ? (
                            <Check className="w-3 h-3 text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    )}
                    
                    {/* Footer with date and actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
                      <span className="text-slate-500 text-xs font-mono">
                        {new Date(build.createdAt).toLocaleString()}
                      </span>
                      <div className="flex items-center gap-1">
                        {build.status === 'COMPLETED' && (
                          <a
                            href={buildsApi.download(build.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 rounded-lg transition-all text-sm font-medium"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </a>
                        )}
                        <button
                          onClick={() => handleDelete(build.id)}
                          className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-800/50">
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Status</th>
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Filename</th>
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Engine</th>
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Watermark</th>
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">ZIP Password</th>
                      <th className="text-left py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Created</th>
                      <th className="text-right py-4 px-4 text-slate-500 font-mono text-xs uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {builds.map((build, index) => (
                      <tr 
                        key={build.id} 
                        className="border-b border-slate-800/30 hover:bg-slate-800/30 transition-colors group opacity-0-init animate-fade-in-up"
                        style={{ animationDelay: `${0.1 + index * 0.05}s` }}
                      >
                        <td className="py-4 px-4">
                          {getStatusBadge(build.status)}
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-mono text-white group-hover:text-cyan-400 transition-colors">
                            {build.filename}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-purple-400 font-mono text-sm">
                            {build.engineType}
                            {build.engineVersion && (
                              <span className="text-slate-500"> v{build.engineVersion}</span>
                            )}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          {build.watermark ? (
                            <code className="text-cyan-400 text-sm bg-cyan-500/10 px-2 py-1 rounded-lg border border-cyan-500/20">
                              {build.watermark}
                            </code>
                          ) : (
                            <span className="text-slate-600">—</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {build.zipPassword ? (
                            <div className="flex items-center gap-1">
                              <code className="text-amber-400 text-sm bg-amber-500/10 px-2 py-1 rounded-lg border border-amber-500/20">
                                {build.zipPassword}
                              </code>
                              <button
                                onClick={() => copyPassword(build.zipPassword!)}
                                className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-500 hover:text-white transition-all"
                                title="Copy password"
                              >
                                {copiedPassword === build.zipPassword ? (
                                  <Check className="w-3.5 h-3.5 text-green-400" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-600">—</span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-slate-500 text-sm font-mono">
                          {new Date(build.createdAt).toLocaleString()}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-end gap-1">
                            {build.status === 'COMPLETED' && (
                              <a
                                href={buildsApi.download(build.id)}
                                className="p-2 text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-all hover:scale-110"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </a>
                            )}
                            <button
                              onClick={() => handleDelete(build.id)}
                              className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all hover:scale-110"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mt-6 pt-6 border-t border-slate-800/50">
                  <div className="flex items-center gap-2 order-2 sm:order-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="px-2 sm:px-3"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline">Previous</span>
                    </Button>
                    
                    {/* Show limited page numbers on mobile */}
                    <div className="flex items-center gap-1 sm:gap-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => {
                          // On mobile, show first, last, current and adjacent pages
                          if (totalPages <= 5) return true;
                          return p === 1 || p === totalPages || Math.abs(p - page) <= 1;
                        })
                        .map((p, idx, arr) => (
                          <React.Fragment key={p}>
                            {idx > 0 && arr[idx - 1] !== p - 1 && (
                              <span className="text-slate-500 px-1">...</span>
                            )}
                            <button
                              onClick={() => setPage(p)}
                              className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                                p === page
                                  ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white'
                                  : 'text-slate-500 hover:text-white hover:bg-slate-800'
                              }`}
                            >
                              {p}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                      className="px-2 sm:px-3"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Page indicator for mobile */}
                  <div className="text-xs text-slate-500 font-mono order-1 sm:order-2 sm:hidden">
                    Page {page} of {totalPages}
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
}
